import React, { useContext, useId, useImperativeHandle, useRef, useState } from "react";
import { restoreFocusUnlessDialogOpened } from "shared-web/components/common/FloatingLayer/FloatingLayer";

export interface PopoverContextValue {
  visible: boolean;
  /** Whether the popup is on screen: open, or still playing its closing animation. */
  presented: boolean;
  setVisible: (value: boolean) => void;
  /** The popup's element ID, for `aria-controls` on custom references. */
  floatingId: string;
  refs: {
    reference: React.RefObject<Element | null>;
    floating: React.RefObject<HTMLElement | null>;
  };
  /** Kept for callers that recomputed the position; Base UI tracks the anchor on its own. */
  update: () => void;
  preRendered: boolean;
  prerender: () => void;
  tags: string[];
}

const PopoverContext = React.createContext<PopoverContextValue | undefined>(undefined);

/** The open state and refs of the enclosing popover, menu or tooltip. */
export const usePopover = () => {
  const popoverContext = useContext(PopoverContext);
  return popoverContext;
};

export interface OverlayStateOptions {
  visible?: boolean;
  onVisibleChange?: (value: boolean, event?: Event, reason?: string) => void;
  handleRef?: React.Ref<PopoverContextValue>;
  tags?: string[];
}

/**
 * The open state every overlay shares (Popover, DropdownMenu and Tooltip): controlled or
 * uncontrolled visibility, the popup ID and refs, and the context their parts and children read.
 */
export const useOverlayState = (options: OverlayStateOptions) => {
  const { visible: controlledVisible, onVisibleChange, handleRef, tags } = options;
  const [uncontrolledVisible, setUncontrolledVisible] = useState(false);
  const visible = controlledVisible ?? uncontrolledVisible;
  // Closing starts when `visible` turns false and ends when Base UI reports the closing animation
  // done. Content such as a virtualised list keeps rendering until then.
  const [lastVisible, setLastVisible] = useState(visible);
  const [closing, setClosing] = useState(false);
  if (lastVisible !== visible) {
    setLastVisible(visible);
    setClosing(!visible);
  }
  const floatingId = useId();
  const referenceRef = useRef<Element | null>(null);
  const floatingRef = useRef<HTMLElement | null>(null);

  const setVisible = (nextVisible: boolean, event?: Event, reason?: string) => {
    setUncontrolledVisible(nextVisible);
    onVisibleChange?.(nextVisible, event, reason);
  };

  const context = {
    visible,
    presented: visible || closing,
    setVisible: (value: boolean) => setVisible(value),
    floatingId,
    refs: { reference: referenceRef, floating: floatingRef },
    update: () => {},
    preRendered: false,
    prerender: () => {},
    tags: tags ?? [],
  } satisfies PopoverContextValue;

  useImperativeHandle(handleRef, () => context);

  /** Pass to the Base UI root's `onOpenChangeComplete`. */
  const onOpenChangeComplete = (open: boolean) => {
    if (!open) setClosing(false);
  };

  return { visible, setVisible, context, onOpenChangeComplete };
};

/** `finalFocus` for an overlay's popup, from its context's refs. */
export const getFinalFocus = (context: PopoverContextValue) =>
  restoreFocusUnlessDialogOpened(
    () => context.refs.reference.current,
    () => context.refs.floating.current,
  );

export default PopoverContext;

/**
 * `value` while the overlay is open, and the last open value while it is closed. Content rendered
 * from it stays put through the closing animation, such as a menu whose choice changes the data
 * it shows.
 */
export const useOpenSnapshot = <T,>(value: T, visible: boolean): T => {
  const snapshot = useRef(value);
  if (visible) snapshot.current = value;
  return visible ? value : snapshot.current;
};
